import { Injectable, inject } from '@angular/core';
import { environment } from '../../environments/environment';
import { BaseHelper } from '../baseHelper';
import { LoadingService } from './loading.service';

export class ApiError extends Error {
  constructor(public status: number, override readonly message: string, public data?: any) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiRequestOptions {
  silent?: boolean; // Suppress automatic error toast if caller wants to handle manually
  customHeaders?: Record<string, string>;
  skipGlobalLoader?: boolean;
}

@Injectable({ providedIn: 'root' })
export class ApiService {
  readonly baseUrl = environment.apiUrl;
  private b = inject(BaseHelper);
  private loading = inject(LoadingService);

  private getToken(): string | null {
    try {
      const raw = localStorage.getItem('scs_admin_session');
      if (raw) {
        const session = JSON.parse(raw);
        return session.token || null;
      }
    } catch {
      // ignore
    }
    return null;
  }

  getHeaders(customHeaders: Record<string, string> = {}): Record<string, string> {
    const traceId = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : (Math.random().toString(36).substring(2) + Date.now().toString(36));
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Trace-Id': traceId,
      ...customHeaders,
    };
    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }

  async get<T = any>(endpoint: string, params?: Record<string, any>, options?: ApiRequestOptions): Promise<T> {
    let url = `${this.baseUrl}${endpoint.startsWith('/') ? endpoint : '/' + endpoint}`;
    if (params) {
      const sp = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== null && v !== '') {
          sp.append(k, String(v));
        }
      }
      const qs = sp.toString();
      if (qs) url += (url.includes('?') ? '&' : '?') + qs;
    }

    if (!options?.skipGlobalLoader) this.loading.start();
    try {
      const res = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(options?.customHeaders),
      });
      return await this.handleResponse<T>(res, options);
    } catch (err: any) {
      this.handleNetworkError(err, options);
      throw err;
    } finally {
      if (!options?.skipGlobalLoader) this.loading.stop();
    }
  }

  async post<T = any>(endpoint: string, body?: any, options?: ApiRequestOptions): Promise<T> {
    const url = `${this.baseUrl}${endpoint.startsWith('/') ? endpoint : '/' + endpoint}`;
    if (!options?.skipGlobalLoader) this.loading.start();
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(options?.customHeaders),
        body: body ? JSON.stringify(body) : undefined,
      });
      return await this.handleResponse<T>(res, options);
    } catch (err: any) {
      this.handleNetworkError(err, options);
      throw err;
    } finally {
      if (!options?.skipGlobalLoader) this.loading.stop();
    }
  }

  async put<T = any>(endpoint: string, body?: any, options?: ApiRequestOptions): Promise<T> {
    const url = `${this.baseUrl}${endpoint.startsWith('/') ? endpoint : '/' + endpoint}`;
    if (!options?.skipGlobalLoader) this.loading.start();
    try {
      const res = await fetch(url, {
        method: 'PUT',
        headers: this.getHeaders(options?.customHeaders),
        body: body ? JSON.stringify(body) : undefined,
      });
      return await this.handleResponse<T>(res, options);
    } catch (err: any) {
      this.handleNetworkError(err, options);
      throw err;
    } finally {
      if (!options?.skipGlobalLoader) this.loading.stop();
    }
  }

  async delete<T = any>(endpoint: string, options?: ApiRequestOptions): Promise<T> {
    const url = `${this.baseUrl}${endpoint.startsWith('/') ? endpoint : '/' + endpoint}`;
    if (!options?.skipGlobalLoader) this.loading.start();
    try {
      const res = await fetch(url, {
        method: 'DELETE',
        headers: this.getHeaders(options?.customHeaders),
      });
      return await this.handleResponse<T>(res, options);
    } catch (err: any) {
      this.handleNetworkError(err, options);
      throw err;
    } finally {
      if (!options?.skipGlobalLoader) this.loading.stop();
    }
  }

  async upload<T = any>(endpoint: string, file: File, options?: ApiRequestOptions): Promise<T> {
    const url = `${this.baseUrl}${endpoint.startsWith('/') ? endpoint : '/' + endpoint}`;
    const fd = new FormData();
    fd.append('file', file);

    const token = this.getToken();
    const headers: Record<string, string> = { ...(options?.customHeaders || {}) };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    if (!options?.skipGlobalLoader) this.loading.start();
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers,
        body: fd,
      });
      return await this.handleResponse<T>(res, options);
    } catch (err: any) {
      this.handleNetworkError(err, options);
      throw err;
    } finally {
      if (!options?.skipGlobalLoader) this.loading.stop();
    }
  }

  private handleNetworkError(err: any, options?: ApiRequestOptions) {
    if (options?.silent) return;
    if (err instanceof ApiError) return; // Handled in handleResponse

    const msg = err?.message || 'Network connection failed. Please verify API server status.';
    this.b.toast(msg, 5000, 'danger');
  }

  private async handleResponse<T>(res: Response, options?: ApiRequestOptions): Promise<T> {
    let json: any = null;
    try {
      json = await res.json();
    } catch {
      // not json
    }

    if (!res.ok) {
      if (res.status === 401) {
        // Token expired or unauthorized
        localStorage.removeItem('scs_admin_session');
        if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
          window.location.href = '/login?session_expired=1';
        }
      }

      const rawMsg = json?.error?.message || json?.message || json?.error || `Request failed with HTTP status ${res.status}`;
      const userFriendlyMsg = this.formatErrorMessage(rawMsg, res.status);

      if (!options?.silent && res.status !== 401) {
        this.b.toast(userFriendlyMsg, 5000, 'danger');
      }

      throw new ApiError(res.status, userFriendlyMsg, json);
    }

    return json as T;
  }

  private formatErrorMessage(raw: string, status: number): string {
    if (status === 429) {
      return 'Rate limit reached: Too many requests. Please wait a moment before trying again.';
    }

    if (typeof raw !== 'string') return `Request failed (${status})`;
    
    // Map common backend error codes to human-friendly messages
    switch (raw) {
      case 'RATE_LIMIT_EXCEEDED':
      case 'too_many_requests':
        return 'Rate limit exceeded: Too many attempts. Please slow down and try again later.';
      case 'forbidden_permission':
      case 'forbidden':
        return 'Access denied: You do not have permissions for this action.';
      case 'user_disabled_or_not_found':
        return 'Account is disabled or not found. Please contact an administrator.';
      case 'session_revoked':
        return 'Your session was revoked. Please log in again.';
      case 'invalid_or_expired_token':
      case 'UNAUTHORIZED':
        return 'Your login session has expired. Please sign in again.';
      case 'NOT_FOUND':
        return 'Requested record or endpoint was not found.';
      case 'SERVER_ERROR':
        return 'Internal server error occurred. Please try again.';
      default:
        if (typeof raw === 'string') {
          if (raw.toLowerCase().includes('hall') && (raw.includes('already exists') || raw.includes('duplicate'))) {
            const match = raw.match(/\((?:lower\(trim\(both from hall\)\)|hall)\)=\(([^)]+)\)/i);
            const val = match ? ` "${match[1]}"` : '';
            return `Specific Hall / Section${val} already exists. Please enter a different hall name.`;
          }
          if (raw.toLowerCase().includes('name') && raw.includes('already exists') && raw.startsWith('Key (name)=')) {
            const match = raw.match(/\(name\)=\(([^)]+)\)/i);
            const val = match ? ` "${match[1]}"` : '';
            return `Venue / Location Name${val} already exists.`;
          }
          if (raw.startsWith('Key (')) {
            return raw.replace(/Key \(([^)]+)\)=\(([^)]+)\) already exists\./i, '$1 "$2" already exists.');
          }
        }
        return raw;
    }
  }
}



